import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Gift } from "lucide-react";
import { Reveal, Label } from "./Motion";
import { OCCASIONS, HAMPER_ITEMS } from "../data";

export const HamperBuilder = ({ onContinue }) => {
  const [occasion, setOccasion] = useState(null);
  const [items, setItems] = useState([]);

  const toggle = (id) =>
    setItems((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const ready = occasion && items.length > 0;

  const submit = () => {
    const labels = HAMPER_ITEMS.filter((i) => items.includes(i.id)).map((i) => i.label);
    onContinue({ kind: "hamper", occasion, items: labels });
  };

  return (
    <section id="builder" data-testid="hamper-builder" className="relative py-24 md:py-36 bg-[#2c2826] text-[#F7F5F0] pc-grain overflow-hidden">
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="max-w-2xl mb-14">
          <Reveal><Label className="text-[#c9a06f]">Custom Hamper Builder</Label></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              Compose your own<br /><span className="italic text-[#E8942E]">signature hamper.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-[#c8beb4] leading-relaxed">
              Choose the occasion, hand-pick the pieces, and we&rsquo;ll wrap it into
              something unforgettable. Send us your selection and we&rsquo;ll take it from there.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-8 space-y-12">
            <div>
              <p className="font-serif text-2xl mb-5"><span className="text-[#8b7e76] mr-3">01</span>Choose an occasion</p>
              <div className="flex flex-wrap gap-3">
                {OCCASIONS.map((o) => (
                  <button
                    key={o}
                    data-testid={`occasion-${o.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setOccasion(o)}
                    className={`rounded-full px-5 py-2.5 text-sm border transition-colors duration-300 ${
                      occasion === o
                        ? "bg-[#E8942E] border-[#E8942E] text-[#2c2826]"
                        : "border-[#4a453f] text-[#c8beb4] hover:border-[#c9a06f] hover:text-[#F7F5F0]"
                    }`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="font-serif text-2xl mb-5"><span className="text-[#8b7e76] mr-3">02</span>Select your pieces</p>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {HAMPER_ITEMS.map((it) => {
                  const active = items.includes(it.id);
                  return (
                    <button
                      key={it.id}
                      data-testid={`item-${it.id}`}
                      onClick={() => toggle(it.id)}
                      className={`group flex items-center justify-between gap-3 px-5 py-4 text-left border transition-colors duration-300 ${
                        active ? "border-[#E8942E] bg-[#E8942E]/10" : "border-[#4a453f] hover:border-[#c9a06f]"
                      }`}
                    >
                      <span className={active ? "text-[#F7F5F0]" : "text-[#c8beb4]"}>{it.label}</span>
                      <span className={`grid place-items-center h-6 w-6 rounded-full border transition-colors duration-300 ${
                        active ? "bg-[#E8942E] border-[#E8942E] text-[#2c2826]" : "border-[#6b655d] text-transparent"
                      }`}>
                        <Check size={14} strokeWidth={3} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 border border-[#4a453f] bg-[#332e29]/60 backdrop-blur-sm p-7">
              <div className="flex items-center gap-3 text-[#c9a06f]">
                <Gift size={18} />
                <Label className="text-[#c9a06f]">Your hamper</Label>
              </div>
              <p className="mt-5 font-serif text-3xl">
                {occasion || <span className="text-[#6b655d]">Occasion</span>}
              </p>
              <div className="mt-5 min-h-[80px]">
                <AnimatePresence mode="popLayout">
                  {items.length === 0 ? (
                    <motion.p key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="text-sm text-[#8b7e76]">No pieces selected yet.</motion.p>
                  ) : (
                    <ul className="space-y-2">
                      {HAMPER_ITEMS.filter((i) => items.includes(i.id)).map((i) => (
                        <motion.li key={i.id} layout initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                          className="flex items-center gap-2 text-sm text-[#e7ddcf]">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#E8942E]" />{i.label}
                        </motion.li>
                      ))}
                    </ul>
                  )}
                </AnimatePresence>
              </div>
              <button
                data-testid="builder-continue"
                disabled={!ready}
                onClick={submit}
                className={`mt-6 w-full rounded-full py-3.5 text-sm transition-colors duration-300 ${
                  ready ? "bg-[#E8942E] text-[#2c2826] hover:bg-[#F7F5F0]" : "bg-[#4a453f] text-[#8b7e76] cursor-not-allowed"
                }`}
              >
                {ready ? "Continue to enquiry \u2192" : "Pick an occasion & a piece"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
